import {
  Component,
  ElementRef,
  forwardRef,
  Host,
  HostListener,
  Input,
  OnInit,
  Optional,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { NgxMaskDirective } from "ngx-mask";
import { EdInputFieldModule } from "@edenred/components/input";
import {
  CdkScrollable,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";

@Component({
  selector: "er-time-picker",
  templateUrl: "./time-picker.component.html",
  styleUrls: ["./time-picker.component.scss"],
  imports: [EdInputFieldModule, MatIconModule, NgxMaskDirective, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class TimePickerComponent implements OnInit, ControlValueAccessor {
  @ViewChild("timePickerInput")
  public timePickerInput!: ElementRef;
  @ViewChild("timePickerData")
  public timePickerData!: TemplateRef<any>;
  @Optional()
  @Host()
  public scrollContainer!: CdkScrollable;

  @Input() invalid: boolean | undefined;

  public timeList: string[] = [];
  public value: string | null = null;
  public onChange: any = () => {};
  public onTouched: any = () => {};
  public timePickerDataId: string | "" = "";
  public isDisabled: boolean | false = false;

  private overlayRef: OverlayRef | null = null;
  private isVisible: boolean | false = false;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  @HostListener("document:click", ["$event"])
  public onDocumentClick(event: MouseEvent): void {
    const timePickerData = document.getElementById(this.timePickerDataId);
    if (timePickerData) {
      const timePickerInputPosition =
        this.timePickerInput.nativeElement.getBoundingClientRect();
      const timePickerDataPosition = timePickerData.getBoundingClientRect();
      const clickPosition = {
        x: event.clientX,
        y:
          timePickerDataPosition.bottom <= timePickerInputPosition.top
            ? event.clientY - timePickerInputPosition.height
            : event.clientY + timePickerInputPosition.height,
      };

      if (
        clickPosition.x < timePickerDataPosition.left ||
        clickPosition.x > timePickerDataPosition.right ||
        clickPosition.y < timePickerDataPosition.top ||
        clickPosition.y > timePickerDataPosition.bottom
      ) {
        this.hideTimePickerData();
      }
    }
  }

  public ngOnInit(): void {
    this.buildTimeList();
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  public toggleTimePickerData(): void {
    if (!this.isVisible) {
      this.displayTimePickerData();
    }
  }

  public onTimeClick(time: string): void {
    this.value = time;
    this.onChange(this.value);
    this.hideTimePickerData();
  }

  public onValueChange(event: any): void {
    for (let charIndex = 0; charIndex < event.length; charIndex++) {
      let maxValue: number | null = null;
      switch (charIndex) {
        case 0:
          maxValue = 2;
          break;
        case 1:
          maxValue = 3;
          break;
        case 2:
          maxValue = 5;
          break;
        default:
          maxValue = null;
      }
      if (this.value && maxValue) {
        if (
          (charIndex == 0 && <number>event[charIndex] > maxValue) ||
          (charIndex == 1 &&
            <number>event[charIndex] > maxValue &&
            <number>event[charIndex - 1] == 2) ||
          (charIndex == 2 && <number>event[charIndex] > maxValue)
        ) {
          this.value =
            this.value.substring(0, charIndex) +
            maxValue +
            this.value.substring(charIndex + this.value.length);
        }
      }
    }
  }

  public focusOut(e: any) {
    this.onChange(e.target.value);
  }

  public onKeyPress(event: any): void {
    const character: string = String.fromCharCode(event.charCode);
    if (!this.isNumeric(character)) {
      event.preventDefault();
    }
  }

  private buildTimeList(): void {
    const stepMinutes: string[] = ["00", "30"];
    for (let index = 0; index < 24; index++) {
      const hour = index < 10 ? `0${index}` : `${index}`;
      stepMinutes.forEach((minute) => {
        this.timeList.push(`${hour}:${minute}`);
      });
    }
  }

  private displayTimePickerData(): void {
    this.timePickerDataId = this.id;
    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.timePickerInput.nativeElement)
      .setOrigin(this.timePickerInput.nativeElement)
      .withPositions([
        {
          originX: "start",
          originY: "bottom",
          overlayX: "start",
          overlayY: "top",
        },
        {
          originX: "start",
          originY: "top",
          overlayX: "start",
          overlayY: "bottom",
        },
      ])
      .withFlexibleDimensions(false)
      .withPush(false)
      .withScrollableContainers([this.scrollContainer]);

    this.overlayRef = this.overlay.create({
      positionStrategy: positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    this.overlayRef.backdropClick().subscribe(() => {
      this.hideTimePickerData();
    });
    this.overlayRef.attach(
      new TemplatePortal(this.timePickerData, this.viewContainerRef)
    );

    this.isVisible = true;
  }

  private hideTimePickerData(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.isVisible = false;
    }
  }

  private isNumeric(character: string[0]): boolean {
    const regex = /^[0-9]$/;
    return regex.test(character);
  }

  public get inputWidth(): number {
    return this.timePickerInput.nativeElement.getBoundingClientRect().width;
  }

  private get id(): string {
    return window.crypto.randomUUID();
  }
}
